/**
 * GoEnHub 機能ツリーの正本（Single Source of Truth）。
 *
 * - サイドバー（admin-web/components/layout/Sidebar.tsx）の表示
 * - 権限マトリックス UI（Settings > ロール設定）
 * - usePermission / AccessGate のアクセス判定
 *
 * いずれもこの FEATURES 配列を起点にする。ここに無い機能 ID は
 * 自動クリーンアップ Trigger により rolePermissions から除去される。
 *
 * 仕様書: docs/01_spec/spec_user_management.md
 *
 * v3.3 IA 刷新 (2026-05):
 * - 4 セクション + 14 親メニュー (Hub) に再編。
 * - Hub アイテムは `tabs` を持ち、ID は `_hub.*` プレフィックス（権限非対象）。
 * - 既存リーフ featureId は完全保持（rolePermissions の互換性確保）。
 * - 一部のタブは同一 featureId を共有（例: 営業設定 / 商談ボードの内部分割）。
 */

import type { FeatureGroup, FeatureItem } from './constants';

/** Hub の識別プレフィックス。listLeafFeatureIds 等で除外される。 */
export const HUB_ID_PREFIX = '_hub.';

export const FEATURES: readonly FeatureGroup[] = [
  // ─────────────────────────────────────────────
  // 🏠 メイン
  // ─────────────────────────────────────────────
  {
    id: 'main',
    label: 'メイン',
    items: [
      {
        id: 'dashboard',
        label: 'ダッシュボード',
        path: '/',
        icon: 'LayoutDashboard',
        actions: ['view'],
      },
      // 📌 掲示板 (v3.5): 社内メンバー限定 (外部SA非表示は rules / callable 側で担保)
      {
        id: 'board.posts',
        label: '掲示板',
        path: '/board',
        icon: 'Pin',
        actions: ['view', 'create', 'edit', 'delete'],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 🎯 マーケティング
  // ─────────────────────────────────────────────
  {
    id: 'marketing',
    label: 'マーケティング',
    emoji: '🎯',
    items: [
      // ✨ キャンペーン Hub
      {
        id: '_hub.campaigns',
        label: 'キャンペーン',
        path: '/campaigns',
        icon: 'Sparkles',
        actions: ['view'],
        emoji: '✨',
        tabs: [
          {
            id: 'campaigns.list',
            tabKey: 'list',
            label: 'ファネル一覧',
            path: '/campaigns?tab=list',
            icon: 'Workflow',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'campaigns.list',
            tabKey: 'templates',
            label: 'ファネル雛形',
            path: '/campaigns?tab=templates',
            icon: 'Bookmark',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'campaigns.landings',
            tabKey: 'landings',
            label: 'ランディング',
            path: '/campaigns?tab=landings',
            icon: 'LayoutTemplate',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'campaigns.forms',
            tabKey: 'forms',
            label: 'フォーム',
            path: '/campaigns?tab=forms',
            icon: 'ClipboardList',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'campaigns.referral',
            tabKey: 'referral',
            label: 'リフェラル',
            path: '/campaigns?tab=referral',
            icon: 'Link',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'campaigns.ad-images',
            tabKey: 'ad-images',
            label: '広告画像',
            path: '/campaigns?tab=ad-images',
            icon: 'ImagePlus',
            actions: ['view', 'create', 'edit', 'delete'],
          },
        ],
      },

      // 📧 メール Hub
      // v3.3.1: 「生成」タブは廃止。新規メール作成はメール一覧 (mail.list) からの
      // 画面遷移で /mail/generate (メール作成/編集画面) を開く形に統一。
      // mail.generate featureId は permission 管理外 (route は維持)。
      {
        id: '_hub.mail',
        label: 'メール',
        path: '/mail',
        icon: 'Mail',
        actions: ['view'],
        emoji: '📧',
        tabs: [
          {
            id: 'mail.list',
            tabKey: 'list',
            label: 'メール',
            path: '/mail?hub=list',
            icon: 'Mail',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'mail.schedules',
            tabKey: 'schedules',
            label: '配信予約',
            path: '/mail?hub=schedules',
            icon: 'CalendarClock',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'mail.analysis',
            tabKey: 'analysis',
            label: '意図設定',
            path: '/mail?hub=analysis',
            icon: 'Atom',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'mail.settings',
            tabKey: 'settings',
            label: '設定',
            path: '/mail?hub=settings',
            icon: 'MailPlus',
            actions: ['view', 'edit'],
          },
        ],
      },

      // 💬 LINE Bot Hub
      {
        id: '_hub.line',
        label: 'LINE Bot',
        path: '/line',
        icon: 'MessagesSquare',
        actions: ['view'],
        emoji: '💬',
        tabs: [
          {
            id: 'line.news',
            tabKey: 'news',
            label: 'ニュース',
            path: '/line?tab=news',
            icon: 'Megaphone',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'line.deliveries',
            tabKey: 'deliveries',
            label: '配信',
            path: '/line?tab=deliveries',
            icon: 'Send',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'line.events',
            tabKey: 'events',
            label: 'イベント',
            path: '/line?tab=events',
            icon: 'Calendar',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'line.offerings',
            tabKey: 'offerings',
            label: '商材',
            path: '/line?tab=offerings',
            icon: 'Package',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'line.qa-review',
            tabKey: 'qa-review',
            label: 'QAレビュー',
            path: '/line?tab=qa-review',
            icon: 'ClipboardCheck',
            actions: ['view', 'edit'],
          },
          {
            id: 'line.settings',
            tabKey: 'settings',
            label: '設定',
            path: '/line?tab=settings',
            icon: 'Settings',
            actions: ['view', 'edit'],
          },
        ],
      },

      // 👥 オーディエンス Hub (旧 mail.contacts + marketing.leads + line.users を統合)
      {
        id: '_hub.audience',
        label: 'オーディエンス',
        path: '/audience',
        icon: 'Users',
        actions: ['view'],
        emoji: '👥',
        tabs: [
          {
            id: 'mail.contacts',
            tabKey: 'contacts',
            label: 'メール配信先',
            path: '/audience?tab=contacts',
            icon: 'Mail',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'mail.contacts',
            tabKey: 'groups',
            label: 'グループ',
            path: '/audience?tab=groups',
            icon: 'Users',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'marketing.leads',
            tabKey: 'leads',
            label: 'リード',
            path: '/audience?tab=leads',
            icon: 'Sparkles',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'line.users',
            tabKey: 'line',
            label: 'LINEフォロワー',
            path: '/audience?tab=line',
            icon: 'UserCircle',
            actions: ['view'],
          },
        ],
      },

      // 🧠 AI プロンプト Hub
      {
        id: '_hub.prompts',
        label: 'AIプロンプト',
        path: '/prompts',
        icon: 'Atom',
        actions: ['view'],
        emoji: '🧠',
        tabs: [
          {
            id: 'prompts.templates',
            tabKey: 'templates',
            label: 'テンプレート',
            path: '/prompts?tab=templates',
            icon: 'FileStack',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'prompts.presets',
            tabKey: 'presets',
            label: 'プレセット',
            path: '/prompts?tab=presets',
            icon: 'Bookmark',
            actions: ['view', 'create', 'edit', 'delete'],
          },
        ],
      },

      // ❓ お問合せ自動化 Hub (Part C / spec_inquiry_automation.md)
      {
        id: '_hub.inquiry',
        label: 'お問合せ',
        path: '/inquiry',
        icon: 'MessageCircleQuestion',
        actions: ['view'],
        emoji: '❓',
        tabs: [
          {
            id: 'inquiry.queue',
            tabKey: 'queue',
            label: '承認キュー',
            path: '/inquiry?tab=queue',
            icon: 'Inbox',
            actions: ['view', 'edit'],
          },
          {
            id: 'inquiry.qa',
            tabKey: 'qa',
            label: 'QA 管理',
            path: '/inquiry?tab=qa',
            icon: 'BookOpen',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'inquiry.settings',
            tabKey: 'settings',
            label: '設定',
            path: '/inquiry?tab=settings',
            icon: 'Settings2',
            actions: ['view', 'edit'],
          },
          {
            id: 'inquiry.dataRetention',
            tabKey: 'data-retention',
            label: 'データ保持',
            path: '/inquiry?tab=data-retention',
            icon: 'Database',
            actions: ['view', 'edit'],
          },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 💼 セールス
  // ─────────────────────────────────────────────
  {
    id: 'sales',
    label: 'セールス',
    emoji: '💼',
    items: [
      // 🗂️ 商談 Hub (旧 sales.deals + system.stale-deals + system.deals-trash)
      // ※ 「統合候補」は商談ボード内部タブとして既存実装のまま (DealsInboxContent)
      {
        id: '_hub.deals',
        label: '商談',
        path: '/deals',
        icon: 'Kanban',
        actions: ['view'],
        emoji: '🗂️',
        tabs: [
          {
            id: 'sales.deals',
            tabKey: 'board',
            label: '商談ボード',
            path: '/deals?tab=board',
            icon: 'Kanban',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'system.stale-deals',
            tabKey: 'stale',
            label: '段階解禁',
            path: '/deals?tab=stale',
            icon: 'AlertTriangle',
            actions: ['view', 'edit'],
          },
          {
            id: 'system.deals-trash',
            tabKey: 'trash',
            label: 'ゴミ箱',
            path: '/deals?tab=trash',
            icon: 'Trash2',
            actions: ['view', 'edit', 'delete'],
          },
        ],
      },

      // 🤝 顧客 (sales.contacts)
      // 2026-10-07: 個別説明を独立メニューへ移した結果タブが 1 つになったため、Hub をやめて独立メニューに
      // (タブの帯を出さない)。featureId は据え置きで権限設定を引き継ぐ
      {
        id: 'sales.contacts',
        label: '顧客',
        path: '/customers',
        icon: 'UserCheck',
        actions: ['view', 'create', 'edit', 'delete'],
      },

      // 🎤 個別説明 (2026-10-07: 顧客 Hub のタブから独立メニューへ。featureId は据え置きで権限設定を引き継ぐ)
      {
        id: 'sales.briefings',
        label: '個別説明',
        path: '/briefings',
        icon: 'Presentation',
        actions: ['view', 'create', 'edit', 'delete'],
      },

      // 📦 受発注 Hub
      {
        id: '_hub.orders',
        label: '受発注',
        path: '/orders',
        icon: 'ShoppingBag',
        actions: ['view'],
        emoji: '📦',
        tabs: [
          {
            id: 'sales.quotes',
            tabKey: 'quotes',
            label: '見積',
            path: '/orders?tab=quotes',
            icon: 'FileText',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'sales.invoices',
            tabKey: 'invoices',
            label: '請求',
            path: '/orders?tab=invoices',
            icon: 'Receipt',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'sales.purchase-orders',
            tabKey: 'po',
            label: '発注',
            path: '/orders?tab=po',
            icon: 'ShoppingCart',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'sales.deliveries',
            tabKey: 'shipping',
            label: '発送',
            path: '/orders?tab=shipping',
            icon: 'Truck',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'sales.sold-devices',
            tabKey: 'devices',
            label: '稼働機器',
            path: '/orders?tab=devices',
            icon: 'Cpu',
            actions: ['view', 'edit'],
          },
        ],
      },

      // 💰 経理 Hub
      {
        id: '_hub.finance',
        label: '経理',
        path: '/finance',
        icon: 'Banknote',
        actions: ['view'],
        emoji: '💰',
        tabs: [
          {
            id: 'sales.commission-ledger',
            tabKey: 'ledger',
            label: '報酬台帳',
            path: '/finance?tab=ledger',
            icon: 'BookOpen',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.sa-invoices',
            tabKey: 'invoices',
            label: 'SA請求書',
            path: '/finance?tab=invoices',
            icon: 'Receipt',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.transfer-requests',
            tabKey: 'transfers',
            label: '送金依頼',
            path: '/finance?tab=transfers',
            icon: 'Send',
            actions: ['view', 'edit'],
          },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 🎯 セールス専用 (外部 SA 向け独立)
  // ─────────────────────────────────────────────
  {
    id: 'sa-portal',
    label: 'セールス専用',
    emoji: '🎯',
    items: [
      {
        id: '_hub.sa',
        label: 'SA ポータル',
        path: '/sa',
        icon: 'Briefcase',
        actions: ['view'],
        emoji: '🎯',
        tabs: [
          {
            id: 'sa.dashboard',
            tabKey: 'dashboard',
            label: 'ダッシュボード',
            path: '/sa?tab=dashboard',
            icon: 'LayoutDashboard',
            actions: ['view'],
          },
          {
            id: 'sa.contacts',
            tabKey: 'contacts',
            label: '自分の顧客',
            path: '/sa?tab=contacts',
            icon: 'UserRound',
            actions: ['view', 'create'],
          },
          {
            id: 'sa.deals',
            tabKey: 'deals',
            label: '担当商談',
            path: '/sa?tab=deals',
            icon: 'Kanban',
            actions: ['view', 'create'],
          },
          {
            id: 'sa.briefings',
            tabKey: 'briefings',
            label: '個別説明',
            path: '/sa?tab=briefings',
            icon: 'Presentation',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'sa.forms',
            tabKey: 'forms',
            label: 'フォーム',
            path: '/sa?tab=forms',
            icon: 'ClipboardList',
            actions: ['view'],
          },
          {
            id: 'sa.catalogs',
            tabKey: 'catalogs',
            label: '価格表',
            path: '/sa?tab=catalogs',
            icon: 'Tag',
            actions: ['view'],
          },
          {
            id: 'sa.documents',
            tabKey: 'documents',
            label: '資料',
            path: '/sa?tab=documents',
            icon: 'BookOpen',
            actions: ['view'],
          },
          {
            id: 'sa.commissions',
            tabKey: 'commissions',
            label: '報酬計算',
            path: '/sa?tab=commissions',
            icon: 'Calculator',
            actions: ['view'],
          },
          {
            id: 'sa.profile',
            tabKey: 'profile',
            label: 'プロフィール',
            path: '/sa?tab=profile',
            icon: 'UserCog',
            actions: ['view', 'edit'],
          },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 🏷 マスタ・設定 (末尾集約)
  // ─────────────────────────────────────────────
  {
    id: 'master',
    label: 'マスタ・設定',
    emoji: '🏷',
    items: [
      // 商品 Hub (商品 + セット + 報酬設定)
      {
        id: '_hub.master-products',
        label: '商品',
        path: '/master/products',
        icon: 'Boxes',
        actions: ['view'],
        tabs: [
          {
            id: 'master.products',
            tabKey: 'catalogs',
            label: '商品',
            path: '/master/products?tab=catalogs',
            icon: 'Boxes',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'master.product-bundles',
            tabKey: 'bundles',
            label: 'セット商品',
            path: '/master/products?tab=bundles',
            icon: 'Package',
            actions: ['view', 'create', 'edit', 'delete'],
          },
          {
            id: 'sales.commissions',
            tabKey: 'commissions',
            label: '報酬設定',
            path: '/master/products?tab=commissions',
            icon: 'Coins',
            actions: ['view', 'create', 'edit', 'delete'],
          },
        ],
      },

      // 📑 資料
      {
        id: 'master.documents',
        label: '資料',
        path: '/documents',
        icon: 'BookOpen',
        actions: ['view', 'create', 'edit', 'delete'],
      },

      // 🛡 同意書 (v3.2.25)
      {
        id: 'master.consent-documents',
        label: '同意書',
        path: '/consent-documents',
        icon: 'ShieldCheck',
        actions: ['view', 'create', 'edit', 'delete'],
      },

      // ⚙️ 営業・通知設定 Hub (旧 SalesSettingsPage の 7 内部タブを Hub レベルに昇格)
      {
        id: '_hub.sales-config',
        label: '営業・通知設定',
        path: '/master/sales-config',
        icon: 'BellRing',
        actions: ['view'],
        tabs: [
          {
            id: 'sales.settings',
            tabKey: 'deadline',
            label: '書類期限',
            path: '/master/sales-config?tab=deadline',
            icon: 'CalendarClock',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.settings',
            tabKey: 'auto-mail',
            label: '自動メール',
            path: '/master/sales-config?tab=auto-mail',
            icon: 'MailPlus',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.settings',
            tabKey: 'mail-templates',
            label: '業務通知',
            path: '/master/sales-config?tab=mail-templates',
            icon: 'Mail',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.settings',
            tabKey: 'chat-endpoints',
            label: 'チャット通知',
            path: '/master/sales-config?tab=chat-endpoints',
            icon: 'MessagesSquare',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.settings',
            tabKey: 'categories',
            label: 'カテゴリー',
            path: '/master/sales-config?tab=categories',
            icon: 'Tag',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.settings',
            tabKey: 'sa-cycle',
            label: '販売代理店',
            path: '/master/sales-config?tab=sa-cycle',
            icon: 'Briefcase',
            actions: ['view', 'edit'],
          },
          {
            id: 'sales.settings',
            tabKey: 'consent',
            label: '同意書',
            path: '/master/sales-config?tab=consent',
            icon: 'ShieldCheck',
            actions: ['view', 'edit'],
          },
        ],
      },

      // 🎯 SA 管理 (v3.2.30): 外部セールスエージェントのオンボーディング・契約・通知 (owner 限定)
      {
        id: 'system.sa-management',
        label: 'SA管理',
        path: '/sales/sa-management',
        icon: 'UserCog',
        actions: ['view', 'edit'],
      },

      // 👤 ユーザー (旧 _hub.admin の "ユーザー" タブから独立)
      {
        id: 'system.users',
        label: 'ユーザー',
        path: '/admin/users',
        icon: 'Users',
        actions: ['view', 'create', 'edit', 'delete'],
      },

      // ⚙️ システム (旧 _hub.admin の "全体設定" を単独メニュー化)
      // 監査ログは SettingsPage 内部タブとして既に提供 (旧 system.logs は廃止)
      {
        id: 'system.settings',
        label: 'システム',
        path: '/settings',
        icon: 'Settings',
        actions: ['view', 'edit'],
      },
    ],
  },
] as const;

// ─────────────────────────────────────────────
// ヘルパー
// ─────────────────────────────────────────────

/** featureId が Hub (構造的ラッパー) であるかを判定。 */
export function isHubFeatureId(featureId: string): boolean {
  return featureId.startsWith(HUB_ID_PREFIX);
}

/**
 * リーフ機能 (= 実権限管理対象) の featureId 一覧を返す。
 * Hub の `_hub.*` ID は除外し、`tabs` を持つ親のタブを展開して収集する。
 * 重複 ID は dedupe される (同一 featureId を共有する複数タブのため)。
 */
export function listAllFeatureIds(): string[] {
  const ids = new Set<string>();
  for (const group of FEATURES) {
    for (const item of group.items) {
      collectLeafIds(item, ids);
    }
  }
  return Array.from(ids);
}

function collectLeafIds(item: FeatureItem, acc: Set<string>): void {
  if (item.tabs && item.tabs.length > 0) {
    for (const tab of item.tabs) {
      collectLeafIds(tab, acc);
    }
  } else if (!isHubFeatureId(item.id)) {
    acc.add(item.id);
  }
}

/** featureId → 最初に見つかった FeatureItem のルックアップ。Hub もタブも検索対象。 */
export function findFeatureById(
  featureId: string,
): { group: FeatureGroup; item: FeatureItem } | null {
  for (const group of FEATURES) {
    for (const item of group.items) {
      if (item.id === featureId) return { group, item };
      if (item.tabs) {
        for (const tab of item.tabs) {
          if (tab.id === featureId) return { group, item: tab };
        }
      }
    }
  }
  return null;
}

/**
 * 指定 path に対応する FeatureItem を返す。
 * Hub 親 path 完全一致、タブ path 一致 (query 含む)、リーフ path 完全一致の順に探索。
 */
export function findFeatureByPath(
  pathname: string,
): { group: FeatureGroup; item: FeatureItem } | null {
  for (const group of FEATURES) {
    for (const item of group.items) {
      if (item.path === pathname) return { group, item };
      if (item.tabs) {
        for (const tab of item.tabs) {
          if (tab.path === pathname) return { group, item: tab };
        }
      }
    }
  }
  return null;
}

/**
 * RoleSettingsTab 用: 全リーフ機能をフラットに列挙する (Hub をスキップしつつタブを展開)。
 * 同一 featureId のタブが複数ある場合、最初の出現のみ返す。
 */
export function iterateLeafFeatures(): Array<{
  group: FeatureGroup;
  item: FeatureItem;
  parentHub?: FeatureItem;
}> {
  const out: Array<{ group: FeatureGroup; item: FeatureItem; parentHub?: FeatureItem }> = [];
  const seen = new Set<string>();
  for (const group of FEATURES) {
    for (const item of group.items) {
      if (item.tabs && item.tabs.length > 0) {
        for (const tab of item.tabs) {
          if (!seen.has(tab.id) && !isHubFeatureId(tab.id)) {
            seen.add(tab.id);
            out.push({ group, item: tab, parentHub: item });
          }
        }
      } else if (!isHubFeatureId(item.id)) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          out.push({ group, item });
        }
      }
    }
  }
  return out;
}
